import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { api, apiErrorMessage } from "../lib/api";
import { currencyTRY, numberTR } from "../lib/format";
import { Skeleton } from "../components/ui/skeleton";

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/analytics/dashboard");
        setData(res.data);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Skeleton className="h-96 rounded-2xl" />;
  if (!data) return null;

  return (
    <div data-testid="analytics-page">
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
          Analiz & Raporlar
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
          İşletme Performansı
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            Toplam Ciro
          </div>
          <div className="font-display font-black text-3xl text-slate-900 num-tabular mt-1">
            {currencyTRY(data.toplam_ciro)}
          </div>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            Toplam Net Kâr
          </div>
          <div
            className={`font-display font-black text-3xl num-tabular mt-1 ${
              data.toplam_kar >= 0 ? "text-emerald-700" : "text-rose-700"
            }`}
          >
            {currencyTRY(data.toplam_kar)}
          </div>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            Ortalama Stok Günü
          </div>
          <div className="font-display font-black text-3xl text-slate-900 num-tabular mt-1">
            {numberTR(data.ortalama_stok_gun)}{" "}
            <span className="text-base text-stone-500 font-medium">gün</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-stone-200 bg-white p-5">
          <h3 className="font-display font-black text-xl text-slate-900 mb-4">
            Aylık Kâr/Zarar (Son 6 Ay)
          </h3>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={data.monthly_pnl}>
                <CartesianGrid stroke="#f5f5f4" vertical={false} />
                <XAxis dataKey="ay" axisLine={false} tickLine={false} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) =>
                    Math.abs(v) >= 1000
                      ? `${(v / 1000).toFixed(0)}b`
                      : v.toString()
                  }
                  width={50}
                />
                <Tooltip
                  cursor={{ fill: "#fafaf9" }}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e7e5e4",
                    fontFamily: "Karla",
                  }}
                  formatter={(v, name) => [
                    currencyTRY(v),
                    name === "net" ? "Net" : name === "kar" ? "Gelir" : "Gider",
                  ]}
                />
                <Bar dataKey="net" radius={[8, 8, 8, 8]}>
                  {data.monthly_pnl.map((m, i) => (
                    <Cell
                      key={i}
                      fill={m.net >= 0 ? "#059669" : "#e11d48"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <h3 className="font-display font-black text-xl text-slate-900 mb-4">
            En Yüksek Gider Kalemleri
          </h3>
          {data.en_yuksek_giderler.length === 0 ? (
            <div className="text-sm text-stone-500 py-10 text-center">
              Henüz gider yok.
            </div>
          ) : (
            <div className="space-y-3">
              {data.en_yuksek_giderler.map((g) => {
                const max = Math.max(
                  ...data.en_yuksek_giderler.map((x) => x.tutar),
                );
                const pct = (g.tutar / max) * 100;
                return (
                  <div key={g.kategori}>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-slate-800">
                        {g.kategori}
                      </span>
                      <span className="text-stone-500 num-tabular">
                        {currencyTRY(g.tutar)}
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-600 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-3 rounded-2xl border border-stone-200 bg-white p-5">
          <h3 className="font-display font-black text-xl text-slate-900 mb-4">
            Marka Bazlı Satış Dağılımı
          </h3>
          {data.marka_dagilim.length === 0 ? (
            <div className="text-sm text-stone-500 py-10 text-center">
              Henüz satış verisi yok.
            </div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart
                  data={data.marka_dagilim}
                  layout="vertical"
                  margin={{ left: 24 }}
                >
                  <CartesianGrid stroke="#f5f5f4" horizontal={false} />
                  <XAxis type="number" axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="marka"
                    axisLine={false}
                    tickLine={false}
                    width={100}
                  />
                  <Tooltip
                    cursor={{ fill: "#fafaf9" }}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e7e5e4",
                      fontFamily: "Karla",
                    }}
                    formatter={(v) => [`${v} adet`, "Satış"]}
                  />
                  <Bar dataKey="adet" fill="#1e293b" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
